import React, { useEffect, useState } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { Divider } from '@mui/material'
import PropTypes from 'prop-types'
import { Title } from '@/modules/base/components/texts'
import SelectionCard from '@/modules/base/components/selection-card/selection-card'
import ProductMainIcon from '@assets/images/icons/product_main.svg?url'
import ProductDocumentIcon from '@assets/images/icons/product_document.svg?url'
import ProductStaffIcon from '@assets/images/icons/product_staff.svg?url'
import CheckThickIcon from '@assets/images/icons/check_thick.svg'
import lang from '@/lang'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import IconCheck from '@assets/images/icons/check.svg'
import { useSelector } from 'react-redux'
import config from '@/config'
import moment from 'moment'
import { isMeinBuero } from '@/utils/build-env'

const useStyles = makeStyles(theme => ({
  selectionCardContent: {
    paddingTop: 30,
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 40,
    alignItems: 'center',
  },
  selectionCardContainer: ({ isMeinbuero }) => ({
    ...(isMeinbuero ? { maxWidth: 400 } : {}),
  }),
  subscriptionTypeTitle: {
    color: theme.black,
    lineHeight: '40px',
    letterSpacing: '0.32px',
    marginBottom: 20,
    fontSize: 16,
    fontWeight: theme.fontWeights.medium,
  },
  title: {
    color: theme.black,
    marginBottom: 20,
    marginTop: 20,
  },
  text: {
    color: theme.black,
    textAlign: 'center',
    maxWidth: 750,
    margin: '0 auto',
    marginBottom: 40,
    fontSize: theme.fontSizes.default,
  },
  learnMore: {
    cursor: 'pointer',
    color: theme.primary,
    alignSelf: 'center',
  },
  tooltip: {
    height: 'fit-content',
    color: theme.black,
  },
  boxWrapper: {
    paddingBottom: 20,
    display: 'flex',
    gap: 20,
    justifyContent: 'flex-start',
    height: '100%',
  },
  subscriptionTypeDescription: {
    fontSize: theme.fontSizes.default,
  },
  subscriptionTypeBulletPoint: {
    width: '100%',
    textAlign: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  subscriptionTypeHintTitle: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.bold,
    marginBottom: 10,
  },
  bold: {
    fontWeight: theme.fontWeights.bold,
  },
  footerHint: {
    fontSize: theme.fontSizes.smaller,
  },
  divider: {
    height: 1,
    width: 15,
    margin: '5px 0',
    borderColor: theme.borderColor,
  },
}))

const isMeinbuero = isMeinBuero()

const { dateFormat: { client: clientDateFormat } } = config

const subscriptionTypeIcon = {
  document: ProductDocumentIcon,
  main: ProductMainIcon,
  staff: ProductStaffIcon,
}

const SubscriptionTypeSelection = (props) => {
  const {
    kind,
    groupedSubscriptionTypes,
    onChange,
    useBulletPointIcons,
    context,
  } = props

  const classes = useStyles({ isMeinbuero })
  const theme = useTheme()

  const [subscriptionTypes, setSubscriptionTypes] = useState([])
  const { subscriptions } = useSelector(state => state.subscription.details)

  useEffect(() => {
  }, [])

  const onTypeButtonClicked = (type) => {
    if (kind === 'single') {
      setSubscriptionTypes([type])
      onChange && onChange(type)
    } else {
      let newSubscriptionTypes
      if (subscriptionTypes.includes(type)) {
        newSubscriptionTypes = subscriptionTypes.filter(st => st !== type)
      } else {
        newSubscriptionTypes = [...subscriptionTypes, type]
      }
      setSubscriptionTypes(newSubscriptionTypes)
      onChange && onChange(newSubscriptionTypes)
    }
  }

  return (
    <>
      <Title position={'center'} size={'medium'} className={classes.title} marginBottom={'40px'}>{context === 'trialSelect' ? `${lang.subscriptionModal.title}` : lang.subscriptionModal.upgradeTitle}</Title>
      <div className={classes.text}>
        {context === 'trialSelect' ? lang.subscriptionModal.text : lang.subscriptionModal.upgradeText}
      </div>
      <div className={classes.boxWrapper}>
        {Object.keys(groupedSubscriptionTypes).map((sTypeType, index) => {
          let footerChildren
          const hideDivider = context === 'upgrade'
          if (context === 'trialSelect') {
            const active = subscriptionTypes.includes(sTypeType)
            const buttonLabel = active ? lang.subscriptionModal.subscriptionType.button.label.active : lang.subscriptionModal.subscriptionType.button.label.inactive
            const selectionHint = active ? <SVGIcon fill={theme.green} svg={IconCheck} width="20px" height="20px" /> : ''
            footerChildren = (<div className={classes.footer}>
              <div>{selectionHint}</div>
              <ButtonIconSquare
                variant='outline'
                label={buttonLabel}
              />
            </div>)
          } else {
            const relatedSub = subscriptions?.find(s => s.typeOfSubscription === sTypeType)
            const title = relatedSub?.isTrial ? lang.myAccount.subscriptionUpgrade.trialVersion : relatedSub ? relatedSub.title : lang.myAccount.subscriptionUpgrade.noPlan
            const end = relatedSub?.isTrial ? lang.myAccount.subscriptionUpgrade.trialExpiredAt(relatedSub.end ? moment(relatedSub.end).format(clientDateFormat) : '-') : ''
            footerChildren = (
              <>
                <div className={classes.footer}>
                  <div className={classes.footerHint}>{lang.myAccount.subscriptionUpgrade.actualSubscription}</div>
                  <div></div>
                </div>
                <div className={classes.footer}>
                  <div className={classes.bold}>{title}</div>
                  <div dangerouslySetInnerHTML={{ __html: end }}></div>
                </div>
              </>
            )
          }
          return (
            <SelectionCard
              title={isMeinbuero ? groupedSubscriptionTypes[sTypeType].headline : ''}
              cardIcon={subscriptionTypeIcon[sTypeType]}
              className={classes.subscriptionTypeTitle}
              classNameContainer={classes.selectionCardContainer}
              onClick={() => onTypeButtonClicked(sTypeType)}
              value={sTypeType}
              active={subscriptionTypes.includes(sTypeType)}
              footerChildren={footerChildren}
              hideDivider={hideDivider}
              useCheckedIcon={hideDivider}
              classNameContent={classes.selectionCardContent}
              key={index}
            >
              {isMeinbuero
                ? groupedSubscriptionTypes[sTypeType].items.map((item, index) =>
                  <div className={classes.subscriptionTypeDescription} key={index}>{item.value.value}</div>,
                )
                : lang.subscriptionModal.subscriptionType.types[sTypeType].bulletPoints.map((bulletPoint, index) => (
                  <div className={classes.subscriptionTypeBulletPoint} key={index}>
                    {useBulletPointIcons && <SVGIcon svg={CheckThickIcon} height={'12px'} width={'12px'} fill={'#255bcf'} />}
                    <span dangerouslySetInnerHTML={{ __html: bulletPoint }} />
                    <Divider className={classes.divider} />
                  </div>
                ))}
              <RichTooltip
                key={index}
                placement={'top'}
                width={300}
                content={(
                  <div className={classes.tooltip}>
                    <div className={classes.subscriptionTypeHintTitle}>
                      {lang.subscriptionModal.subscriptionType.types[sTypeType].hint.title}
                    </div>
                    <div className={classes.subscriptionTypeDescription} dangerouslySetInnerHTML={{ __html: lang.subscriptionModal.subscriptionType.types[sTypeType].hint.text }}></div>
                  </div>)
                }
              >
                <div className={classes.learnMore}>
                  {lang.subscriptionModal.learnMore}
                </div>
              </RichTooltip>
            </SelectionCard>
          )
        })}
      </div>
    </>
  )
}

SubscriptionTypeSelection.propTypes = {
  kind: PropTypes.oneOf(['single', 'multi']),
  context: PropTypes.oneOf(['upgrade', 'trialSelect']),
  groupedSubscriptionTypes: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  useBulletPointIcons: PropTypes.bool,
}

SubscriptionTypeSelection.defaultProps = {
  context: 'upgrade',
  groupedSubscriptionTypes: {},
  kind: 'single',
  useBulletPointIcons: true,
}

export default SubscriptionTypeSelection
